/**
 * Consolidated view of one case-worker run: a handful of buckets (depositors,
 * hub, intermediaries, exchange sinks, issuer) joined by aggregated edges, the
 * shape the manual reports use. Fetches the contract response, draws it with
 * its own Cytoscape instance and shows a detail card on hover or keyboard focus.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, AlertTriangle, Copy } from 'lucide-react';
import { caseService } from '../../services/api';
import type { ConsolidatedResponse } from './types';
import { toElements } from './elements';
import { ConsolidatedGraph, type GraphTarget } from './ConsolidatedGraph';
import { ConsolidatedLegend } from './ConsolidatedLegend';
import { DetailCard, type CardItem } from './DetailCard';
import { memberKeys } from './focusEvidence';

type Load =
  | { key: string; status: 'loading' }
  | { key: string; status: 'ok'; data: ConsolidatedResponse }
  | { key: string; status: 'error'; kind: 'notfound' | 'network' };

interface Props {
  caseId: string;
  runId: string;
  /** True when the case has no recorded run yet (a 404 for "latest" then means "no runs"). */
  noRunRecorded?: boolean;
  onBackToEvidence: () => void;
  /** Called with the identifiers of the members to focus once the evidence canvas is showing. */
  onShowOnEvidence: (keys: Set<string>) => void;
}

interface Active { kind: 'bucket' | 'edge'; id: string; x: number; y: number; fromKeyboard?: boolean }

const CARD_W = 320;

export function ConsolidatedView({ caseId, runId, noRunRecorded, onBackToEvidence, onShowOnEvidence }: Props) {
  const key = `${caseId}:${runId}`;
  const [load, setLoad] = useState<Load>({ key, status: 'loading' });
  const [active, setActive] = useState<Active | null>(null);
  const [copied, setCopied] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const wrap = useRef<HTMLDivElement>(null);

  // R8: an answer for a previous case or run never lands on the current one.
  useEffect(() => {
    let cancelled = false;
    setLoad({ key, status: 'loading' });
    setActive(null);
    Promise.resolve()
      .then(() => caseService.getConsolidated(caseId, runId))
      .then((data) => { if (!cancelled) setLoad({ key, status: 'ok', data }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        setLoad({ key, status: 'error', kind: status === 404 ? 'notfound' : 'network' });
      });
    return () => { cancelled = true; };
  }, [caseId, runId, key]);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  const data = load.status === 'ok' && load.key === key ? load.data : null;
  const built = useMemo(() => (data ? toElements(data) : null), [data]);
  const bucketById = useMemo(() => new Map((data?.buckets ?? []).map((b) => [b.id, b])), [data]);
  const edgeById = useMemo(() => new Map((data?.edges ?? []).map((e) => [e.id, e])), [data]);

  const showOnEvidence = (t: { kind: 'bucket' | 'edge'; id: string }) => {
    if (!data) return;
    if (t.kind === 'bucket') {
      const b = bucketById.get(t.id);
      if (b) onShowOnEvidence(memberKeys([b]));
    } else {
      const e = edgeById.get(t.id);
      const ends = e ? [bucketById.get(e.source), bucketById.get(e.target)].filter((b): b is NonNullable<typeof b> => !!b) : [];
      if (ends.length) onShowOnEvidence(memberKeys(ends));
    }
  };

  const hover = (t: GraphTarget | null) => {
    window.clearTimeout(hideTimer.current);
    if (t) setActive(t);
    else hideTimer.current = window.setTimeout(() => setActive((a) => (a?.fromKeyboard ? a : null)), 250);
  };

  const item: CardItem | null = (() => {
    if (!active || !data) return null;
    if (active.kind === 'bucket') {
      const bucket = bucketById.get(active.id);
      return bucket ? { kind: 'bucket', bucket } : null;
    }
    const edge = edgeById.get(active.id);
    if (!edge) return null;
    return {
      kind: 'edge', edge,
      sourceLabel: bucketById.get(edge.source)?.label ?? edge.source,
      targetLabel: bucketById.get(edge.target)?.label ?? edge.target,
    };
  })();
  const narrative = active && data ? data.narrative.find((n) => n.ref === active.id)?.text ?? null : null;

  const copyMermaid = () => {
    if (!data?.mermaid) return;
    try { void navigator.clipboard?.writeText(data.mermaid); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ }
  };

  const back = (
    <button
      type="button"
      onClick={onBackToEvidence}
      data-testid="consol-back"
      className="mt-3 rounded-md border border-border bg-secondary px-3 py-1.5 text-xs hover:bg-secondary/80"
    >
      Back to Evidence
    </button>
  );
  const message = (text: string, tone: 'info' | 'error' = 'info') => (
    <div className="absolute inset-0 grid place-items-center bg-canvas/90 p-6">
      <div className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg" data-testid="consol-message" role={tone === 'error' ? 'alert' : undefined}>
        {tone === 'error' && <AlertTriangle className="mx-auto mb-2 h-4 w-4 text-red-500" aria-hidden />}
        <p className="text-sm">{text}</p>
        {back}
      </div>
    </div>
  );

  if (load.key !== key || load.status === 'loading') {
    return (
      <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground bg-canvas/90" data-testid="consol-loading">
        <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading consolidated view…</span>
      </div>
    );
  }
  if (load.status === 'error') {
    if (load.kind === 'notfound') {
      return message(noRunRecorded && runId === 'latest'
        ? 'No case-worker run has been recorded for this case yet, so there is nothing to consolidate.'
        : 'This run has no consolidated view. It was recorded before the view existed; open the Evidence canvas instead.');
    }
    return message('The consolidated view could not be loaded. Check the connection and try again.', 'error');
  }
  if (!data || !built || built.elements.every((e) => e.group !== 'nodes')) {
    return message('This run has no case spine to consolidate: no wallets were recorded for it.');
  }

  const anchorX = active ? Math.max(8, Math.min(active.x + 16, (wrap.current?.clientWidth ?? 900) - CARD_W - 8)) : 0;
  const anchorY = active ? Math.max(8, active.y + 16) : 0;
  const drawnBuckets = data.buckets.filter((b) => b.role !== 'context');
  const drawnEdges = data.edges.filter((e) => bucketById.get(e.source)?.role !== 'context' && bucketById.get(e.target)?.role !== 'context');

  return (
    <div ref={wrap} className="absolute inset-0 bg-canvas" data-testid="consol-view" data-run-id={data.run_id}>
      <ConsolidatedGraph
        elements={built.elements}
        rootIds={built.rootIds}
        onHover={hover}
        onActivate={(t) => showOnEvidence(t)}
      />

      <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={copyMermaid}
          disabled={!data.mermaid}
          data-testid="consol-copy-mermaid"
          className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-secondary disabled:opacity-60"
        >
          <Copy className="h-3 w-3" aria-hidden /> {copied ? 'Copied' : 'Copy Mermaid'}
        </button>
      </div>

      {built.collapsedWallets > 0 && (
        <div className="absolute right-3 top-3 z-10 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground" data-testid="consol-collapsed-chip">
          {built.collapsedWallets} more wallets collapsed
        </div>
      )}

      <ConsolidatedLegend buckets={drawnBuckets} edges={drawnEdges} />

      {/* Keyboard access: the canvas is not focusable, so every bucket and edge is also a button. */}
      <div
        className="sr-only focus-within:not-sr-only absolute bottom-3 left-3 z-10 max-h-40 max-w-md overflow-y-auto rounded-md border border-border bg-card p-2 text-xs"
        role="group"
        aria-label="Buckets and edges"
        data-testid="consol-items"
      >
        {[...drawnBuckets.map((b) => ({ kind: 'bucket' as const, id: b.id, text: `${b.label}, ${b.member_count} wallets` })),
          ...drawnEdges.map((e) => ({ kind: 'edge' as const, id: e.id, text: `${bucketById.get(e.source)?.label ?? e.source} to ${bucketById.get(e.target)?.label ?? e.target}, ${e.transfers} transfers` }))]
          .map((it) => (
            <button
              key={`${it.kind}:${it.id}`}
              type="button"
              className="block w-full rounded px-1.5 py-0.5 text-left hover:bg-muted focus:bg-muted"
              data-testid={`consol-item-${it.id}`}
              onFocus={() => { window.clearTimeout(hideTimer.current); setActive({ kind: it.kind, id: it.id, x: 12, y: 48, fromKeyboard: true }); }}
              onKeyDown={(ev) => { if (ev.key === 'Escape') setActive(null); }}
              onClick={() => showOnEvidence(it)}
            >
              {it.text}
            </button>
          ))}
      </div>

      {item && (
        <div
          className="absolute z-20"
          style={{ left: anchorX, top: anchorY }}
          onMouseEnter={() => window.clearTimeout(hideTimer.current)}
          onMouseLeave={() => hover(null)}
        >
          <DetailCard item={item} narrative={narrative} />
          <button
            type="button"
            onClick={() => showOnEvidence(active!)}
            data-testid="consol-show-on-evidence"
            className="mt-1 w-full rounded-md border border-border bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-brand-gold-hover"
          >
            Show on evidence canvas
          </button>
        </div>
      )}
    </div>
  );
}
